import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Phone, Mail, AlertCircle, DollarSign, CheckCircle } from 'lucide-react';
import { siteConfig } from '../data/content';

const GoodFaithEstimatePage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <DollarSign size={24} className="text-teal-700" />
            </div>
            <div>
              <h1 className="heading-1" data-testid="gfe-title">Good Faith Estimate</h1>
              <p className="text-sm text-slate-500">Your Right to a Cost Estimate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
              
              {/* Introduction */}
              <div>
                <h2 className="heading-2 mb-4">Your Right to a Good Faith Estimate</h2>
                <p className="body-medium mb-4">
                  You have the right to receive a "Good Faith Estimate" explaining how much your 
                  healthcare services will cost. Under federal law (the No Surprises Act), healthcare 
                  providers must give patients who don't have insurance or who are not using insurance 
                  an estimate of the expected charges for scheduled services.
                </p>
              </div>

              {/* Key Points */}
              <div>
                <h3 className="heading-3 mb-4">What You Should Know</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                    <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-teal-900">Request an Estimate</p>
                      <p className="text-sm text-teal-800">
                        You can ask your healthcare provider for a Good Faith Estimate before 
                        scheduling a service, or at any time during your care.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                    <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-teal-900">Written Estimate</p>
                      <p className="text-sm text-teal-800">
                        If you schedule a healthcare service at least 3 business days in advance, 
                        you must receive a written Good Faith Estimate within 1 business day after scheduling.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                    <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-teal-900">Estimate Contents</p>
                      <p className="text-sm text-teal-800">
                        The estimate should include the expected cost of any items or services 
                        that are reasonably expected to be provided together with the primary 
                        scheduled service.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dispute Rights */}
              <div>
                <h3 className="heading-3 mb-4">If Your Bill Is Higher Than Expected</h3>
                <p className="body-medium mb-4">
                  If you receive a bill that is at least $400 more than your Good Faith Estimate, 
                  you may have the right to dispute the bill. You can start the dispute process 
                  by contacting the provider or initiating a patient-provider dispute resolution process.
                </p>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-800">
                        <strong>Important:</strong> Save a copy of your Good Faith Estimate. 
                        For questions or more information about your right to a Good Faith Estimate, 
                        visit{' '}
                        <a 
                          href="https://www.cms.gov/nosurprises" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          www.cms.gov/nosurprises
                        </a>
                        {' '}or call 1-800-985-3059.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="heading-3 mb-4">Request an Estimate</h3>
                <p className="body-medium mb-4">
                  To request a Good Faith Estimate for your speech therapy services, please contact us:
                </p>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-semibold text-slate-800">{siteConfig.name}</p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-teal-600" />
                      <a href={`tel:${siteConfig.phone}`} className="link text-sm">{siteConfig.phone}</a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-teal-600" />
                      <a href={`mailto:${siteConfig.email}`} className="link text-sm">{siteConfig.email}</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="text-center pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  This page provides general information about your rights under federal law. 
                  It does not constitute a Good Faith Estimate. Please contact us directly to 
                  request an estimate for your specific services.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoodFaithEstimatePage;
